import clsx from 'clsx';
import React, { Fragment } from 'react';

import useCurrentUser from '@hooks/useCurrentUser';
import { Scope } from '@utils/backend-api/search';
import { capitalize } from '@utils/formatting';

const SUBSCRIBER_TAB_NAMES: Array<Scope> = [Scope.Articles, Scope.Forum, Scope.Directory];
// Non-members can only search Articles; Forum and Directory are subscriber-only.
const NON_MEMBER_TAB_NAMES: Array<Scope> = [Scope.Articles];

interface SearchTabsProps {
  onScopeChange?: (scope: Scope) => void;
  query: string;
  scope: Scope;
}

const SearchTabs = ({ onScopeChange, scope }: SearchTabsProps) => {
  const { currentUser } = useCurrentUser();
  const tabNames = currentUser?.is_subscriber ? SUBSCRIBER_TAB_NAMES : NON_MEMBER_TAB_NAMES;
  const numOfTabs = tabNames.length - 1;

  const handleTabClick = (tab: Scope) => {
    if (tab !== scope && onScopeChange) {
      onScopeChange(tab);
    }
  };

  return (
    <div
      className={clsx(
        'mx-32 mb-32 flex min-w-[260px] justify-between border-0 border-b border-solid border-black sm:mx-0 sm:mb-48 dark:border-grayscale-charcoal',
        '!mx-0 !mb-0 mt-40 w-full sm:mt-72 sm:w-[390px]'
      )}
      role="tablist"
      data-testid="tabs"
    >
      {tabNames.map((tab, index) => (
        <Fragment key={`${tab}-${index}`}>
          <button
            type="button"
            role="tab"
            aria-selected={scope === tab}
            onClick={() => handleTabClick(tab)}
            className={clsx(
              'cursor-pointer border-0 bg-transparent p-0 font-suisse-intl-mono text-overline-lg text-primary-black hover:text-primary-magenta dark:hover:text-primary-pink',
              {
                'active pointer-events-none border-0 border-b-[12px] border-solid border-primary-magenta':
                  scope === tab,
              }
            )}
          >
            {capitalize(tab)}
          </button>
          {numOfTabs > index && <div className="-mb-3 w-[1px] rotate-[-30deg] bg-black" />}
        </Fragment>
      ))}
    </div>
  );
};

export default SearchTabs;
